import { useState } from 'react';
import { useLoading } from '../../../../hooks/useLoading';
import { useNotifications } from '../../../../hooks/useNotifications';
import {
  updateRCMRequest,
  updateGapTrackerByGapId
} from '../../../../constants/requests';

/**
 * Custom hook for handling Testing component save operations
 * Manages save logic, gap tracker sync, and RCM updates
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.RCMData - The RCM data to save
 * @param {Function} params.setRCMData - Function to update RCM data state
 * @param {string} params.RCMId - The RCM ID
 * @param {string} params.type - The template type
 * @param {string} params.socScope - The SOC scope (for SOC engagements)
 * @param {Object} params.gapTracker - The gap tracker data
 * @returns {Object} - Save functionality and state
 */
export const useTestingSave = ({
  RCMData,
  setRCMData,
  RCMId,
  type,
  socScope,
  gapTracker
}) => {
  const { showNotifications } = useNotifications();
  const { request } = useLoading();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      let NewRcmData = { ...RCMData };

      // Handle SOC readiness gap tracker sync
      if (
        (type === 'SOC2.0' || type === 'SOC1.0' || type === 'ISAE 3402') &&
        (socScope === 'Readiness SOC1' || socScope === 'Readiness SOC2')
      ) {
        const gapTrackerMeta = gapTracker.data.meta;
        let gapTrackerRows = gapTracker.data.rows['GapTracker'];
        const gapId = gapTracker['_id'];

        RCMData['rows']['Master Table'].map((row, index) => {
          if (
            row['goToGapTracker'] &&
            row['goToGapTracker'] === 'No' &&
            (row['Operating Effectiveness'] === 'Ineffective' ||
              row['Design  Effectiveness'] === 'Ineffective')
          ) {
            gapTrackerRows.push({
              'S.No': gapTrackerRows.length + 1,
              Process: '',
              'Subprocess (Domain)': '',
              Area: '',
              'Process  owner': '',
              'Aumyaa SPOC Name': '',
              Category: '',
              'SOP (Policy Reference)': '',
              'Date on which discussed': '',
              'SCF Number': '',
              'Control Objective': '',
              'Control Number': ``,
              'Risk Number': '',
              Risk: '',
              'CA Number': `${row['Control No.']}`,
              'Control Activity': `${row['Control Activity']}`,
              'Policy Statement': '',
              'Brief Description of Deficiency (Observation)': `${row['Observation']}`,
              Impact: '',
              Recommendation: '',
              'Action plan': '',
              'Expected Date of Closure(Timeline)': '',
              'Current Process / Under Automation': '',
              'Process Owner Name': '',
              'Process Owner Role': '',
              'Process  Owner Email': '',
              'Control Owner Name': '',
              'Control Owner Role': '',
              'Control Owner Email': '',
              'Reliant (Yes/No)': '',
              'Evidence annexure': '',
              'Status (Open/Closed/Drop)': 'Open',
              'Actual Date of closure': '',
              RCMId: `${RCMId}`
            });

            NewRcmData['rows']['Master Table'][index]['goToGapTracker'] = 'Yes';
          }
        });

        const gapTrackerPayload = {
          data: {
            meta: gapTrackerMeta,
            rows: {
              GapTracker: gapTrackerRows
            }
          }
        };

        let response = await request(() =>
          updateGapTrackerByGapId(gapTrackerPayload, gapId)
        );

        if (response.data.statusCode === 200) {
          showNotifications('success', response.data.message, 5000);
        } else {
          showNotifications('error', response.data.message, 5000);
        }
      }

      // Update RCM data
      const payload = {
        data: NewRcmData
      };

      const res = await request(() => updateRCMRequest(RCMId, payload));
      if (res.data.statusCode === 200) {
        setRCMData(NewRcmData);
        showNotifications('success', res.data.message, 5000);
      } else {
        showNotifications('error', res.data.message, 5000);
      }
    } catch (err) {
      showNotifications('error', err.toString(), 5000);
      console.log(err);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    handleSave,
    isSaving
  };
};
